import { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Upload, Loader2, LogOut, CheckCircle2, FileText, Users, Send, Download, Trash2, Inbox, Search, ArrowUpDown, Paperclip } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sp_admin_token";

const QUOTE_SORTS = {
  newest: { label: "Newest first", fn: (a, b) => (b.created_at || "").localeCompare(a.created_at || "") },
  oldest: { label: "Oldest first", fn: (a, b) => (a.created_at || "").localeCompare(b.created_at || "") },
  name: { label: "Name (A–Z)", fn: (a, b) => (a.name || "").localeCompare(b.name || "") },
  project: { label: "Project type", fn: (a, b) => (a.project_type || "").localeCompare(b.project_type || "") },
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(!!token);
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [current, setCurrent] = useState(null);
  const [subCount, setSubCount] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [notifying, setNotifying] = useState(false);
  const [quoteSearch, setQuoteSearch] = useState("");
  const [quoteSort, setQuoteSort] = useState("newest");
  const [confirm, setConfirm] = useState(null); // { title, description, confirmLabel, destructive, action }
  const fileRef = useRef(null);

  const authed = !!token && !checking;

  const loadSubscribers = () =>
    axios
      .get(`${API}/subscribers`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setSubCount(r.data.count);
        setSubscribers(r.data.subscribers || []);
      })
      .catch(() => {});

  const loadQuotes = () =>
    axios
      .get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setQuotes(r.data || []))
      .catch(() => {});

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setChecking(false))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setChecking(false);
      });
  }, [token]);

  useEffect(() => {
    if (authed) {
      axios.get(`${API}/deals/flyer`).then((r) => setCurrent(r.data.flyer)).catch(() => {});
      loadSubscribers();
      loadQuotes();
    }
  }, [authed]);

  const deleteQuote = (id) => {
    setConfirm({
      title: "Delete quote request?",
      description: "This will permanently remove this lead. This action cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
      action: async () => {
        try {
          await axios.delete(`${API}/quotes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          setQuotes((qs) => qs.filter((q) => q.id !== id));
          toast.success("Quote request deleted");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Couldn't delete. Try again.");
        }
      },
    });
  };

  const deleteSubscriber = (id) => {
    setConfirm({
      title: "Remove subscriber?",
      description: "This subscriber will be removed from the mailing list. This action cannot be undone.",
      confirmLabel: "Remove",
      destructive: true,
      action: async () => {
        try {
          await axios.delete(`${API}/subscribers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          setSubscribers((subs) => subs.filter((s) => s.id !== id));
          setSubCount((c) => (typeof c === "number" ? Math.max(0, c - 1) : c));
          toast.success("Subscriber removed");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Couldn't remove. Try again.");
        }
      },
    });
  };

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setChecking(false);
      setPassword("");
      toast.success("Signed in");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Incorrect password");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    toast.success("Signed out");
  };

  const notify = () => {
    if (!current) {
      toast.error("Publish a flyer first, then notify subscribers.");
      return;
    }
    setConfirm({
      title: "Send flyer to subscribers?",
      description: `This week's live flyer will be emailed to ${subCount ?? "all"} subscriber(s).`,
      confirmLabel: "Send now",
      destructive: false,
      action: async () => {
        setNotifying(true);
        try {
          const { data } = await axios.post(
            `${API}/deals/notify`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success(data.message || `Sent to ${data.sent} subscribers.`);
        } catch (err) {
          toast.error(err.response?.data?.detail || "Couldn't send. Try again.");
        } finally {
          setNotifying(false);
        }
      },
    });
  };

  const exportCsv = () => {
    window.open(`${API}/subscribers/export?auth=${encodeURIComponent(token)}`, "_blank");
  };

  const viewQuotePhoto = async (id) => {
    try {
      const res = await axios.get(`${API}/quotes/${id}/photo`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch {
      toast.error("Couldn't load the attached photo.");
    }
  };

  const removeFlyer = () => {
    setConfirm({
      title: "Remove current flyer?",
      description: "The flyer will be taken off the Deals page immediately. Visitors will see a “New deals coming soon” message until you publish a new one.",
      confirmLabel: "Remove flyer",
      destructive: true,
      action: async () => {
        try {
          await axios.delete(`${API}/deals/flyer`, { headers: { Authorization: `Bearer ${token}` } });
          setCurrent(null);
          toast.success("Flyer removed — the Deals page now shows the coming-soon message.");
        } catch (err) {
          if (err.response?.status === 401) {
            logout();
            toast.error("Session expired — please sign in again.");
          } else {
            toast.error(err.response?.data?.detail || "Couldn't remove. Try again.");
          }
        }
      },
    });
  };

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Choose a flyer file first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/deals/flyer`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setCurrent({
        content_type: data.content_type,
        original_filename: data.original_filename,
        file_url: `/api/deals/flyer/file?v=${data.id}`,
      });
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Flyer published — it's now live on the Deals page.");
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        toast.error("Session expired — please sign in again.");
      } else {
        toast.error(err.response?.data?.detail || "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  const filteredQuotes = useMemo(() => {
    const q = quoteSearch.trim().toLowerCase();
    const filtered = q
      ? quotes.filter((item) =>
          [item.name, item.email, item.company, item.project_type, item.message, item.phone]
            .filter(Boolean)
            .some((field) => field.toLowerCase().includes(q))
        )
      : quotes;
    return [...filtered].sort((QUOTE_SORTS[quoteSort] || QUOTE_SORTS.newest).fn);
  }, [quotes, quoteSearch, quoteSort]);

  const inputCls =
    "w-full bg-white/5 border-0 border-b border-white/20 rounded-none px-4 py-4 text-white placeholder-white/30 focus:outline-none focus:border-gold transition-colors duration-300";

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Seo title="Admin | Saver Plus Lighting Corp" description="Admin area." path="/admin" index={false} />
        <Loader2 className="w-6 h-6 text-gold animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center px-5" data-testid="admin-login">
        <Seo title="Admin | Saver Plus Lighting Corp" description="Admin area." path="/admin" index={false} />
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <span className="grid place-items-center w-10 h-10 border border-white/20">
              <Lock className="w-4 h-4 text-gold" />
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/40">Saver Plus</p>
              <h1 className="font-display uppercase text-2xl tracking-tight leading-none">Admin Access</h1>
            </div>
          </div>
          <form onSubmit={login} className="space-y-6">
            <input
              type="password"
              className={inputCls}
              placeholder="Enter admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-8 py-4 hover:bg-[#b00000] transition-colors duration-300 disabled:opacity-60"
            >
              {loggingIn ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : "Sign In"}
            </button>
          </form>
          <p className="mt-6 text-xs text-white/30">This area is restricted. Only authorized staff can upload flyers.</p>
        </div>
      </div>
    );
  }

  const isPdf = current && current.content_type === "application/pdf";
  const currentUrl = current ? `${process.env.REACT_APP_BACKEND_URL}${current.file_url}` : null;

  return (
    <div className="min-h-screen pt-32 pb-24 max-w-[1000px] mx-auto px-5 md:px-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-12">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold mb-2">Admin</p>
          <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight leading-none">Weekly Flyer</h1>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout-button"
          className="inline-flex items-center gap-2 border border-white/20 text-xs uppercase tracking-[0.14em] px-5 py-3 hover:border-gold hover:text-gold transition-colors duration-300"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <form onSubmit={upload} className="border border-white/10 bg-[#121212] p-8 md:p-10">
        <h2 className="font-display uppercase text-2xl tracking-tight mb-2">Upload new flyer</h2>
        <p className="text-white/50 text-sm mb-8">
          Accepts JPG, PNG, WEBP or PDF (max 15MB). The new flyer replaces the current one instantly on the Deals page.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.pdf"
          data-testid="flyer-file-input"
          className="block w-full text-sm text-white/70 file:mr-4 file:py-3 file:px-6 file:border-0 file:bg-white/10 file:text-white file:uppercase file:tracking-[0.12em] file:text-xs hover:file:bg-white/20 file:cursor-pointer cursor-pointer mb-8"
        />
        <button
          type="submit"
          disabled={uploading}
          data-testid="flyer-upload-button"
          className="inline-flex items-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-8 py-4 hover:bg-[#b00000] transition-colors duration-300 disabled:opacity-60"
        >
          {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Publishing…</> : <><Upload className="w-4 h-4" /> Publish Flyer</>}
        </button>
      </form>

      <div className="mt-10">
        <div className="flex items-center gap-2 mb-5">
          <CheckCircle2 className="w-4 h-4 text-gold" />
          <h2 className="font-display uppercase text-xl tracking-tight">Currently live</h2>
        </div>
        {current ? (
          <div>
            {isPdf ? (
              <a href={currentUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gold hover:text-white transition-colors" data-testid="current-flyer">
                <FileText className="w-4 h-4" /> {current.original_filename || "View current flyer (PDF)"}
              </a>
            ) : (
              <img src={currentUrl} alt="Current flyer" className="max-w-md w-full border border-white/10" data-testid="current-flyer" />
            )}
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <a
                href={currentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-white/20 text-xs uppercase tracking-[0.14em] px-5 py-3 hover:border-gold hover:text-gold transition-colors duration-300"
                data-testid="view-current-flyer"
              >
                <FileText className="w-4 h-4" /> View live flyer
              </a>
              <button
                onClick={removeFlyer}
                data-testid="remove-flyer-button"
                className="inline-flex items-center gap-2 border border-white/20 text-xs uppercase tracking-[0.14em] px-5 py-3 hover:border-red-400/60 hover:text-red-400 transition-colors duration-300"
              >
                <Trash2 className="w-4 h-4" /> Remove flyer
              </button>
            </div>
          </div>
        ) : (
          <p className="text-white/40 text-sm" data-testid="no-current-flyer">No flyer published yet. The Deals page is showing the &ldquo;New deals coming soon&rdquo; message.</p>
        )}
      </div>

      {/* Mailing list */}
      <div className="mt-14 border-t border-white/10 pt-12" data-testid="admin-mailing-list">
        <div className="flex items-center gap-2 mb-6">
          <Users className="w-4 h-4 text-gold" />
          <h2 className="font-display uppercase text-xl tracking-tight">Mailing list</h2>
        </div>
        <div className="flex flex-wrap items-center gap-8">
          <div>
            <p className="font-display text-gold text-5xl leading-none" data-testid="subscriber-count">
              {subCount === null ? "—" : subCount}
            </p>
            <p className="mt-2 text-xs uppercase tracking-[0.14em] text-white/40">Subscribers</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={notify}
              disabled={notifying}
              data-testid="notify-button"
              className="inline-flex items-center gap-2 bg-gold text-white text-sm uppercase tracking-[0.14em] font-semibold px-6 py-3.5 hover:bg-[#b00000] transition-colors duration-300 disabled:opacity-60"
            >
              {notifying ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Email flyer to subscribers</>}
            </button>
            <button
              onClick={exportCsv}
              disabled={!subCount}
              data-testid="export-button"
              className="inline-flex items-center gap-2 border border-white/20 text-sm uppercase tracking-[0.14em] px-6 py-3.5 hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-40"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>
        <p className="mt-5 text-xs text-white/35 max-w-lg">
          &ldquo;Email flyer to subscribers&rdquo; sends the current live flyer to everyone on the list. New sign-ups automatically
          receive a welcome email with the latest flyer.
        </p>

        {subscribers.length > 0 && (
          <div className="mt-8 border border-white/10 divide-y divide-white/10" data-testid="subscriber-list">
            {subscribers.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-4 px-4 py-3" data-testid="subscriber-row">
                <div className="min-w-0">
                  <p className="text-white/85 text-sm truncate">{s.email}</p>
                  {s.name ? <p className="text-white/40 text-xs truncate">{s.name}</p> : null}
                </div>
                <button
                  onClick={() => deleteSubscriber(s.id)}
                  data-testid={`delete-subscriber-${s.id}`}
                  title="Remove subscriber"
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-white/40 hover:text-red-400 transition-colors duration-300"
                >
                  <Trash2 className="w-4 h-4" /> Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quote requests */}
      <div className="mt-14 border-t border-white/10 pt-12" data-testid="admin-quotes">
        <div className="flex items-center gap-2 mb-6">
          <Inbox className="w-4 h-4 text-gold" />
          <h2 className="font-display uppercase text-xl tracking-tight">Quote requests</h2>
          <span className="ml-2 text-xs uppercase tracking-[0.14em] text-white/40" data-testid="quote-count">
            ({quoteSearch ? `${filteredQuotes.length} of ${quotes.length}` : quotes.length})
          </span>
        </div>

        {quotes.length === 0 ? (
          <p className="text-white/40 text-sm" data-testid="no-quotes">No quote requests yet.</p>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  value={quoteSearch}
                  onChange={(e) => setQuoteSearch(e.target.value)}
                  placeholder="Search by name, email, company, project…"
                  data-testid="quote-search-input"
                  className="w-full bg-white/5 border border-white/15 rounded-none pl-10 pr-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold transition-colors duration-300"
                />
              </div>
              <div className="relative sm:w-56">
                <ArrowUpDown className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <select
                  value={quoteSort}
                  onChange={(e) => setQuoteSort(e.target.value)}
                  data-testid="quote-sort-select"
                  className="w-full appearance-none bg-white/5 border border-white/15 rounded-none pl-10 pr-4 py-3 text-sm text-white focus:outline-none focus:border-gold transition-colors duration-300 cursor-pointer"
                >
                  {Object.entries(QUOTE_SORTS).map(([key, { label }]) => (
                    <option key={key} value={key} className="bg-[#121212]">{label}</option>
                  ))}
                </select>
              </div>
            </div>

            {filteredQuotes.length === 0 ? (
              <p className="text-white/40 text-sm" data-testid="no-quote-matches">
                No quote requests match &ldquo;{quoteSearch}&rdquo;.
              </p>
            ) : (
              <div className="space-y-4">
                {filteredQuotes.map((q) => (
              <div key={q.id} className="border border-white/10 bg-[#121212] p-5" data-testid="quote-row">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-display uppercase text-lg tracking-tight truncate">{q.name}</p>
                    <p className="text-sm text-white/60 mt-0.5">
                      <a href={`mailto:${q.email}`} className="text-gold hover:text-white transition-colors">{q.email}</a>
                      {q.phone ? <span className="text-white/40"> · {q.phone}</span> : null}
                    </p>
                    {(q.company || q.project_type) && (
                      <p className="text-xs uppercase tracking-[0.1em] text-white/40 mt-1">
                        {q.company}{q.company && q.project_type ? " · " : ""}{q.project_type}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => deleteQuote(q.id)}
                    data-testid={`delete-quote-${q.id}`}
                    title="Delete quote request"
                    className="shrink-0 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-white/40 hover:text-red-400 transition-colors duration-300"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
                <p className="mt-3 text-white/70 text-sm leading-relaxed whitespace-pre-wrap">{q.message}</p>
                {q.photo_path ? (
                  <button
                    onClick={() => viewQuotePhoto(q.id)}
                    data-testid={`view-photo-${q.id}`}
                    className="mt-3 inline-flex items-center gap-2 border border-white/20 text-xs uppercase tracking-[0.12em] px-4 py-2.5 text-white/60 hover:border-gold hover:text-gold transition-colors duration-300"
                  >
                    <Paperclip className="w-3.5 h-3.5" /> View attached photo
                  </button>
                ) : null}
                {q.created_at ? (
                  <p className="mt-3 text-xs text-white/30">{new Date(q.created_at).toLocaleString()}</p>
                ) : null}
              </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        onConfirm={() => { confirm?.action?.(); setConfirm(null); }}
        title={confirm?.title}
        description={confirm?.description}
        confirmLabel={confirm?.confirmLabel}
        destructive={confirm?.destructive}
      />
    </div>
  );
}
